import React, { useRef } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { IconSearch } from '../../../components';

import css from './MainPanelHeader.module.css';

/**
 * Text input that lets shoppers filter results by keyword. Submitting pushes `?keywords=`
 * to the URL (or clears it when the field is emptied); it does not filter as-you-type.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.initialKeywords - Current `keywords` value from the URL, if any
 * @param {Function} props.onSubmit - Called with the trimmed input value on submit
 * @returns {JSX.Element}
 */
export const KeywordSearchBar = props => {
  const { initialKeywords, onSubmit } = props;
  const intl = useIntl();
  const inputRef = useRef(null);

  const handleSubmit = e => {
    e.preventDefault();
    onSubmit(inputRef.current?.value.trim() || '');
  };

  return (
    <form className={css.keywordSearch} onSubmit={handleSubmit} role="search">
      <input
        className={css.keywordSearchInput}
        type="search"
        name="keywords"
        ref={inputRef}
        defaultValue={initialKeywords || ''}
        placeholder={intl.formatMessage({ id: 'MainPanelHeader.keywordsPlaceholder' })}
        aria-label={intl.formatMessage({ id: 'MainPanelHeader.keywordsPlaceholder' })}
      />
      <button
        className={css.keywordSearchSubmit}
        type="submit"
        aria-label={intl.formatMessage({ id: 'MainPanelHeader.keywordsSubmit' })}
      >
        <IconSearch className={css.keywordSearchIcon} />
      </button>
    </form>
  );
};

/**
 * MainPanelHeader component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {React.Node} props.children - The children
 * @param {React.Node} props.sortByComponent - The sort by component
 * @param {boolean} props.isSortByActive - Whether the sort by is active
 * @param {boolean} props.listingsAreLoaded - Whether the listings are loaded
 * @param {number} props.resultsCount - The results count
 * @param {boolean} props.searchInProgress - Whether the search is in progress
 * @param {React.Node} props.noResultsInfo - The no results info
 * @returns {JSX.Element}
 */
const MainPanelHeader = props => {
  const {
    rootClassName,
    className,
    children,
    sortByComponent,
    isSortByActive,
    listingsAreLoaded,
    resultsCount,
    searchInProgress = false,
    noResultsInfo,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <div className={css.searchOptions}>
        <h1 className={css.searchResultSummary}>
          <span className={css.resultsFound}>
            {searchInProgress ? (
              <FormattedMessage id="MainPanelHeader.loadingResults" />
            ) : (
              <FormattedMessage
                id="MainPanelHeader.foundResults"
                values={{ count: resultsCount }}
              />
            )}
          </span>
        </h1>
        {isSortByActive ? (
          <div className={css.sortyByWrapper}>
            <span className={css.sortyBy}>
              <FormattedMessage id="MainPanelHeader.sortBy" />
            </span>
            {sortByComponent}
          </div>
        ) : null}
      </div>

      {children}

      {noResultsInfo ? noResultsInfo : null}
    </div>
  );
};

export default MainPanelHeader;
